import { useTranslation } from "react-i18next";
import { Building2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Guard for tenant-scoped pages.
 *
 * When the caller has no active tenant (typical for a platform owner viewing
 * the "Platforma" context), we render a clear empty state instead of the
 * page body. Because the wrapped page never mounts, its `useEffect` API
 * calls never fire — this prevents the flood of `400 no_active_tenant`
 * responses that would otherwise surface as red error banners across the app.
 *
 * The CTA opens the tenant switcher in the top bar (identified by
 * `data-testid="tenant-switcher-btn"`), so the guidance is actionable.
 */
export default function RequireTenant({ children }: { children: React.ReactNode }) {
    const { t } = useTranslation();
    const { activeTenantId, activeMembership, me } = useAuth();

    // Platform owners always pass — in the "Platforma" context they see
    // a global cross-tenant view of every list page. See
    // deps.require_perm platform-view mode for the backend counterpart.
    if (me?.user?.is_platform_owner) return <>{children}</>;

    // For non-platform users: only require an active tenant. Membership
    // resolution is handled server-side.
    if (activeTenantId && activeMembership) return <>{children}</>;

    const openSwitcher = () => {
        const btn = document.querySelector<HTMLButtonElement>(
            '[data-testid="tenant-switcher-btn"]'
        );
        btn?.click();
        btn?.focus();
    };

    return (
        <div
            data-testid="require-tenant-empty"
            className="flex items-center justify-center py-20 px-6"
        >
            <div className="max-w-md text-center space-y-6">
                <div
                    className="mx-auto rounded-full p-4 inline-flex"
                    style={{ background: "var(--selected-bg)", color: "var(--primary)" }}
                >
                    <Building2 size={40} strokeWidth={1.5} />
                </div>
                <div className="space-y-2">
                    <h1
                        className="text-2xl font-semibold"
                        style={{ color: "var(--text)" }}
                        data-testid="require-tenant-title"
                    >
                        {t("no_tenant.title")}
                    </h1>
                    <p
                        className="text-sm leading-relaxed"
                        style={{ color: "var(--text-muted)" }}
                    >
                        {t("no_tenant.description")}
                    </p>
                </div>
                <button
                    data-testid="require-tenant-cta"
                    onClick={openSwitcher}
                    className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium"
                    style={{
                        background: "var(--primary)",
                        color: "var(--primary-fg)",
                        minHeight: 44,
                    }}
                >
                    <Building2 size={16} />
                    {t("no_tenant.cta")}
                </button>
            </div>
        </div>
    );
}
